import {
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import type { CrmListFilterCategory, CrmListFilterOption } from './crm-list-filters-dropdown.model';

@Component({
  selector: 'app-crm-list-filters-dropdown',
  standalone: true,
  templateUrl: './crm-list-filters-dropdown.component.html',
  styleUrl: './crm-list-filters-dropdown.component.scss',
})
export class CrmListFiltersDropdownComponent {
  private readonly host = inject(ElementRef<HTMLElement>).nativeElement;

  readonly assignedToValue = input('');
  readonly statusValue = input('');
  readonly destinationValue = input('');
  readonly assignedToOptions = input<CrmListFilterOption[]>([]);
  readonly statusOptions = input<CrmListFilterOption[]>([]);
  readonly destinationOptions = input<CrmListFilterOption[]>([]);

  readonly assignedToChange = output<string>();
  readonly statusChange = output<string>();
  readonly destinationChange = output<string>();
  readonly cleared = output<void>();

  readonly open = signal(false);
  readonly activeCategory = signal<CrmListFilterCategory | null>(null);

  readonly activeCount = computed(() => {
    let count = 0;
    if (this.assignedToValue().trim()) count += 1;
    if (this.statusValue().trim()) count += 1;
    if (this.destinationValue().trim()) count += 1;
    return count;
  });

  readonly triggerLabel = computed(() => {
    const count = this.activeCount();
    return count > 0 ? `Filters (${count})` : 'Filters';
  });

  readonly assignedToLabel = computed(() =>
    this.resolveLabel(this.assignedToValue(), this.assignedToOptions()),
  );
  readonly statusLabel = computed(() =>
    this.resolveLabel(this.statusValue(), this.statusOptions()),
  );
  readonly destinationLabel = computed(() =>
    this.resolveLabel(this.destinationValue(), this.destinationOptions()),
  );

  readonly nestedOptions = computed(() => {
    const category = this.activeCategory();
    if (!category) return [] as CrmListFilterOption[];
    if (category === 'assignedTo') return this.withAllOption(this.assignedToOptions());
    if (category === 'status') return this.withAllOption(this.statusOptions());
    return this.withAllOption(this.destinationOptions());
  });

  readonly nestedTitle = computed(() => {
    const category = this.activeCategory();
    if (category === 'assignedTo') return 'Assigned to';
    if (category === 'status') return 'Status';
    if (category === 'destination') return 'Destination';
    return 'Filters';
  });

  readonly nestedSelectedValue = computed(() => {
    const category = this.activeCategory();
    if (category === 'assignedTo') return this.assignedToValue();
    if (category === 'status') return this.statusValue();
    if (category === 'destination') return this.destinationValue();
    return '';
  });

  toggleOpen(): void {
    if (this.open()) {
      this.close();
      return;
    }
    this.open.set(true);
    this.activeCategory.set(null);
  }

  close(): void {
    this.open.set(false);
    this.activeCategory.set(null);
  }

  openCategory(category: CrmListFilterCategory): void {
    this.activeCategory.set(category);
  }

  backToCategories(): void {
    this.activeCategory.set(null);
  }

  selectOption(value: string): void {
    const category = this.activeCategory();
    if (!category) return;
    if (category === 'assignedTo') this.assignedToChange.emit(value);
    if (category === 'status') this.statusChange.emit(value);
    if (category === 'destination') this.destinationChange.emit(value);
    this.activeCategory.set(null);
  }

  clearFilters(): void {
    if (this.activeCount() === 0) return;
    this.cleared.emit();
    this.activeCategory.set(null);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.open()) return;
    const target = event.target as Node | null;
    if (target && this.host.contains(target)) return;
    this.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.open()) return;
    if (this.activeCategory()) {
      this.activeCategory.set(null);
      return;
    }
    this.close();
  }

  private resolveLabel(value: string, options: CrmListFilterOption[]): string {
    const trimmed = value.trim();
    if (!trimmed) return 'All';
    return options.find((option) => option.value === trimmed)?.label ?? trimmed;
  }

  private withAllOption(options: CrmListFilterOption[]): CrmListFilterOption[] {
    return [{ value: '', label: 'All' }, ...options];
  }
}
